import useSWR from "swr";

import { useActingAsOverrideHeader } from "src/auth";
import { useTokenRefreshHandler } from "src/hooks";
import { useUserService } from "src/services";
import { getUserToken } from "src/utils/jwtToken";
import { GuestStatus, Invite } from "src/types";

export type WebinarGuestPaging = {
  limit: number;
  offset: number;
};

export type WebinarGuestFilter = {
  guestStatusFilter: GuestStatus | "";
  followUpStatusFilter: string;
  paging?: WebinarGuestPaging;
};

export type WebinarMeetingDefinition = {
  id: number;
  name: string;
  enabled: boolean;
  inviteStyle: string;
};

export type WebinarResponse = {
  id: number;
  active: boolean;
  status: string;
  startTime?: string;
  endTime?: string;
  startTimeOverridden?: boolean;
  duration: number;
  lastActivity?: string;
  mostRecentSysNote?: string;
  host?: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
  };
  meetingDefinition?: WebinarMeetingDefinition;
  guests?: any[];
  guestsTotal: number;
  attribution?: any;
};

export function useEventDetails(
  id: string,
  query: Partial<WebinarGuestFilter>,
) {
  const service = useUserService();
  const tokenRefreshHandler = useTokenRefreshHandler();

  const accessToken = getUserToken();
  const override = useActingAsOverrideHeader();

  const headers: { [index: string]: string } = {
    "JWT-TOKEN": accessToken as string,
  };

  if (override) {
    headers.override = override;
  }

  const { data, error } = useSWR([`/api/webinars/${id}`, query], async () => {
    const webinar = (await service
      .post(`/api/webinars/${id}`)
      .set(headers)
      .send(query)
      .then(tokenRefreshHandler)
      .then((res: Response) => res.body)) as WebinarResponse;

    const invite = (await service
      .get(`/api/meetings/instance/invite/${id}`)
      .set(headers)
      .then(tokenRefreshHandler)
      .then((res: Response) => res.body)) as Invite;

    return {
      ...webinar,
      location: invite.location,
    };
  });

  return {
    data: data as WebinarResponse & { location: string },
    error,
    loading: !error && !data,
  };
}
